import React, { useEffect, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import {
  fetchMyBookingDetails,
  rescheduleOrCancelBooking,
} from "../../services/api";
import { BOOKING_STATUS } from "../../utils/constants";
import Loader from "../common/Loader";
import "../common/Account.css";

// Match backend / checkout: moment format "h:mm:ss a"
const TIME_SLOTS = Array.from({ length: 24 }, (_, hour) => {
  const period = hour >= 12 ? "pm" : "am";
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:00:00 ${period}`;
});

const BookingDetails = () => {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchMyBookingDetails(bookingId);
      const data = res?.data;
      setBooking(data);
      setDate(data?.bookingDate || "");
      setTime(data?.bookingTime || "");
    } catch (err) {
      setError(err.message || "Failed to load booking");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const toApiDate = (value) => {
    // input type=date is YYYY-MM-DD → DD/MM/YYYY
    if (!value) return "";
    if (value.includes("/")) return value;
    const [y, m, d] = value.split("-");
    return `${d}/${m}/${y}`;
  };

  const fromApiDate = (value) => {
    if (!value) return "";
    if (value.includes("-") && value.length === 10) return value;
    const [d, m, y] = value.split("/");
    if (!d || !m || !y) return "";
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  };

  const reschedule = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await rescheduleOrCancelBooking({
        bookingId,
        bookingDate: toApiDate(date),
        bookingTime: time,
      });
      setSuccess("Booking rescheduled");
      await load();
    } catch (err) {
      setError(err.message || "Reschedule failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="account-page">
        <Loader fullPage message="Loading booking..." />
      </div>
    );
  }

  return (
    <div className="account-page">
      {saving && <Loader overlay message="Updating..." />}
      <div className="coustom_container">
        <Link to="/my-bookings" className="btn btn-kior-outline mb-3">
          ← Back to bookings
        </Link>
        <h1>Booking details</h1>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        {!booking ? (
          <div className="account-card">Booking not found.</div>
        ) : (
          <div className="account-card">
            <p>
              <strong>ID:</strong> {booking.bookingId || booking._id}
            </p>
            <p>
              <strong>Date:</strong> {booking.bookingDate} · {booking.bookingTime}
            </p>
            <p>
              <strong>Amount:</strong> ₹{booking.finalAmount ?? booking.amount}
            </p>
            {booking.testIds?.length > 0 && (
              <div className="mb-3">
                <strong>Tests:</strong>
                <ul>
                  {booking.testIds.map((t) => (
                    <li key={t._id || t}>{t.name || t}</li>
                  ))}
                </ul>
              </div>
            )}
            {booking.packageId?.length > 0 && (
              <div className="mb-3">
                <strong>Packages:</strong>
                <ul>
                  {booking.packageId.map((p) => (
                    <li key={p._id || p}>{p.name || p}</li>
                  ))}
                </ul>
              </div>
            )}

            {booking.status !== BOOKING_STATUS.CANCELLED &&
              booking.status !== BOOKING_STATUS.COMPLETED && (
                <Form onSubmit={reschedule} className="mt-4">
                  <h2 className="h5">Reschedule</h2>
                  <Form.Group className="mb-2">
                    <Form.Label>Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={fromApiDate(date)}
                      onChange={(e) => setDate(e.target.value)}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Time</Form.Label>
                    <Form.Select
                      value={TIME_SLOTS.includes(time) ? time : ""}
                      onChange={(e) => setTime(e.target.value)}
                      required
                    >
                      <option value="">Select time</option>
                      {!TIME_SLOTS.includes(time) && time ? (
                        <option value={time}>{time}</option>
                      ) : null}
                      {TIME_SLOTS.map((slot) => (
                        <option key={slot} value={slot}>
                          {slot}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Button type="submit" className="btn-kior">
                    Save new slot
                  </Button>
                </Form>
              )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingDetails;
